
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { Star, MessageSquare, ArrowLeft, Award, User, ChevronRight, Share2, Globe, ShieldCheck } from 'lucide-react';
import { useAppContext } from '../App.jsx';
import { USERS_COLLECTION, COLORS } from '../config/constants';
import AuthLoadingScreen from './AuthLoadingScreen.jsx';
import { getRankData } from '../components/client/ServiDetailModal.jsx';
import TerviLogo from '../components/common/TerviLogo.jsx';
import ShareProfileModal from '../components/profile/ShareProfileModal.jsx';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const PublicServiProfileScreen = () => {
    const { serviId } = useParams();
    const navigate = useNavigate();
    const { db, user, getOrCreateChat, showMessage } = useAppContext();

    const [serviData, setServiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('about');
    const [reviews, setReviews] = useState([]);
    const [openLightbox, setOpenLightbox] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    useEffect(() => {
        const fetchServi = async () => {
            if (!db || !serviId) return;
            try {
                const docRef = doc(db, USERS_COLLECTION, serviId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.roles?.servi) {
                        setServiData({ id: docSnap.id, ...data });
                    } else {
                        showMessage("Perfil não encontrado.", "error");
                        navigate('/');
                    }
                } else {
                    showMessage("Profissional não existe.", "error");
                    navigate('/');
                }
            } catch (error) {
                console.error("Erro ao buscar perfil público:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchServi();
    }, [serviId, db]);

    useEffect(() => {
        if (activeTab === 'reviews' && serviId && db) {
            const fetchReviews = async () => {
                try {
                    const ratingsRef = collection(db, USERS_COLLECTION, serviId, 'ratings');
                    const q = query(ratingsRef, orderBy('createdAt', 'desc'), limit(10));
                    const snap = await getDocs(q);
                    setReviews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                } catch (e) { console.error(e); }
            };
            fetchReviews();
        }
    }, [activeTab, serviId, db]);

    const handleAction = async () => {
        if (!user) {
            showMessage("Faça login para solicitar um orçamento.", "info");
            navigate('/login');
            return;
        }

        const chatId = await getOrCreateChat(serviId, "Solicitação via Perfil Público");
        if (chatId) navigate(`/chat/${chatId}`);
    };

    if (loading) return <AuthLoadingScreen message="Abrindo vitrine profissional..." />;
    if (!serviData) return null;

    const profile = serviData.profile_servi || {};
    const rank = getRankData(profile.completedServices || 0);
    const displayName = profile.companyName || profile.name || 'Profissional';
    const slides = (profile.gallery || []).map(item => ({ src: item.url }));

    return (
        <div className="h-full bg-gray-50 flex flex-col overflow-y-auto">
            {/* Header Fixo com TerviLogo */}
            <div className="sticky top-0 bg-white/80 backdrop-blur-md z-30 p-4 border-b flex justify-between items-center">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                    <ArrowLeft size={24} className="text-gray-700" />
                </button>

                <TerviLogo size={24} />

                <button onClick={() => setIsShareModalOpen(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                    <Share2 size={24} className="text-gray-700" />
                </button>
            </div>

            <div className="p-6">
                {/* Perfil Header */}
                <div className="flex flex-col items-center text-center mb-8">
                    <div className="w-32 h-32 rounded-[2.5rem] overflow-hidden border-4 border-white shadow-2xl mb-4 bg-white p-1">
                        <img src={profile.photoUrl || `https://ui-avatars.com/api/?name=${displayName.replace(/ /g, '+')}&background=F3F4F6&color=9CA3AF`} className="w-full h-full object-contain rounded-[2rem]" alt="" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-800 leading-tight mb-1">{displayName}</h1>
                    <p className="text-blue-600 font-bold text-sm uppercase tracking-widest">{profile.mainCategory}</p>

                    <div className="flex items-center mt-4 space-x-4">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center text-yellow-400">
                                <Star size={18} className="fill-current" />
                                <span className="ml-1 text-gray-800 font-black text-lg">{(profile.rating || 0).toFixed(1)}</span>
                            </div>
                            <span className="text-[9px] font-bold text-gray-400 uppercase">{profile.ratingCount || 0} avaliações</span>
                        </div>
                        <div className="h-8 w-[1px] bg-gray-200"></div>
                        <div className="flex flex-col items-center">
                            <span className="text-gray-800 font-black text-lg">{profile.completedServices || 0}</span>
                            <span className="text-[9px] font-bold text-gray-400 uppercase">Atendimentos</span>
                        </div>
                    </div>

                    <div className={`mt-4 flex items-center px-4 py-1 rounded-full border shadow-sm ${rank.bg} ${rank.text}`}>
                        <Award size={14} className="mr-2" />
                        <span className="text-[10px] font-black uppercase tracking-widest">{rank.label}</span>
                    </div>
                </div>

                {/* Abas */}
                <div className="flex bg-white p-1 rounded-2xl shadow-sm border border-gray-100 mb-6">
                    <button onClick={() => setActiveTab('about')} className={`flex-1 py-3 text-xs font-black uppercase rounded-xl transition-all ${activeTab === 'about' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400'}`}>Sobre</button>
                    <button onClick={() => setActiveTab('portfolio')} className={`flex-1 py-3 text-xs font-black uppercase rounded-xl transition-all ${activeTab === 'portfolio' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400'}`}>Trabalhos</button>
                    <button onClick={() => setActiveTab('reviews')} className={`flex-1 py-3 text-xs font-black uppercase rounded-xl transition-all ${activeTab === 'reviews' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400'}`}>Notas</button>
                </div>

                {/* Conteúdo dinâmico */}
                <div className="min-h-[200px] mb-24">
                    {activeTab === 'about' && (
                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 text-left">
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center"><User size={14} className="mr-2" /> Descrição do Profissional</h3>
                            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{profile.description || 'Nenhuma descrição informada.'}</p>
                        </div>
                    )}

                    {activeTab === 'portfolio' && (
                        <div className="grid grid-cols-2 gap-3">
                            {profile.gallery?.length > 0 ? profile.gallery.map((item, index) => (
                                <div key={index} className="aspect-square bg-white p-1 rounded-2xl shadow-sm border border-gray-100 cursor-pointer overflow-hidden" onClick={() => { setLightboxIndex(index); setOpenLightbox(true); }}>
                                    <img src={item.url} className="w-full h-full object-cover rounded-xl" alt="" />
                                </div>
                            )) : <p className="col-span-2 text-center text-gray-400 text-xs py-10 uppercase font-bold">Sem fotos no portfólio</p>}
                        </div>
                    )}

                    {activeTab === 'reviews' && (
                        <div className="space-y-3">
                            {reviews.length > 0 ? reviews.map(r => (
                                <div key={r.id} className="bg-white p-4 rounded-2xl border border-gray-100 text-left">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-xs font-bold text-gray-800">{r.raterName}</span>
                                        <div className="flex text-yellow-400"><Star size={12} className="fill-current" /> <span className="text-[10px] ml-1 font-bold">{r.rating}</span></div>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">"{r.comment}"</p>
                                </div>
                            )) : <p className="text-center text-gray-400 text-xs py-10 uppercase font-bold">Nenhuma avaliação</p>}
                        </div>
                    )}
                </div>
            </div>

            {/* Ação Flutuante */}
            <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent z-40 max-w-lg mx-auto">
                <button
                    onClick={handleAction}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-[1.5rem] shadow-2xl shadow-blue-200 flex items-center justify-center space-x-3 active:scale-95 transition-all"
                >
                    <MessageSquare size={20} />
                    <span className="uppercase tracking-widest text-sm">Solicitar Orçamento</span>
                </button>
            </div>

            <Lightbox open={openLightbox} close={() => setOpenLightbox(false)} index={lightboxIndex} slides={slides} />
            {isShareModalOpen && (
                <ShareProfileModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    serviData={serviData.profile_servi}
                    serviId={serviId}
                    showMessage={showMessage}
                />
            )}
        </div>
    );
};

export default PublicServiProfileScreen;
